import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * SRAM text input (CRCB2B-633). Two size steps on the system-wide scale —
 * `lg` 48px (default) and `md` 40px. There is deliberately no `sm`: the design
 * defines no 32px field. Label typography does not change between steps; only
 * the control height and the icon size do.
 *
 * The resting box carries a --muted fill, not a transparent one, and a 1px
 * --border boundary. That pairing is a known WCAG SC 1.4.11 failure (no
 * adjacency reaches 3:1) — built as designed and escalated, not silently
 * substituted. See the form-inputs spec.
 */
function Input({
  className,
  type,
  size = "lg",
  ...props
}: Omit<React.ComponentProps<"input">, "size"> & { size?: "lg" | "md" }) {
  return (
    <input
      type={type}
      data-slot="input"
      data-size={size}
      className={cn(
        "file:text-foreground text-input-foreground placeholder:text-placeholder selection:bg-primary selection:text-primary-foreground border-input h-12 w-full min-w-0 rounded-ui border bg-muted px-4 py-3 text-base leading-[1.5] tracking-[0.03em] shadow-ui transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium data-[size=md]:h-10",
        "hover:border-border-hover",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        // Figma's Invalid variant recolors the border only — the label stays
        // --foreground and there is no ring, so the upstream ring tint is dropped.
        "aria-invalid:border-border-error",
        // Disabled is a token recolor, not a fade: the border and text repoint while
        // the icons hold at full strength, which upstream's disabled:opacity-50
        // (deliberately gone) could not express.
        //
        // DEVIATION from Figma, on purpose: node 5845:29413 declares NO fill on the
        // disabled box, only a #f5f5f5 border. On the white page that renders as a
        // white field behind a 1.09:1 boundary — an invisible control. We keep the
        // resting --muted fill instead, so disabled reads as a flat filled block.
        // Note the button does the opposite and DOES have a designed disabled fill
        // (--disabled #bcbcbc), so the omission here looks like an oversight rather
        // than an intent. Raised with design; see the form-inputs spec.
        "disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-border-disabled disabled:text-disabled-foreground disabled:placeholder:text-disabled-foreground",
        // Readonly is a different box, not a state tint: no fill, no boundary except
        // a hairline bottom rule, no inset, and content height rather than 48/40px.
        // Keyed off [readonly] rather than Tailwind's read-only: variant, which also
        // matches :disabled and would cross-apply this to disabled fields.
        "[&[readonly]]:h-auto [&[readonly]]:border-transparent [&[readonly]]:border-b-border-subtle [&[readonly]]:bg-transparent [&[readonly]]:px-0 [&[readonly]]:py-2 [&[readonly]]:[--ui-radius:0] [&[readonly]]:[--ui-shadow:0_0_#0000]",
        className
      )}
      {...props}
    />
  )
}

export { Input }
